import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Archive,
  ArchiveRestore,
  FileSearch,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, apiError } from "@/lib/api";
import { ADMIN_NAV } from "./nav";

const blank = {
  family_id: "",
  partner_id: "",
  legal_name: "",
  date_of_birth: "",
  grade_level: "",
  academic_year: "",
  state_of_residence: "",
};

export default function AdminStudents() {
  const [students, setStudents] = useState(null);
  const [families, setFamilies] = useState([]);
  const [partners, setPartners] = useState([]);
  const [search, setSearch] = useState("");
  const [archived, setArchived] = useState(false);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/students", { params: { search, archived } });
    setStudents(data);
  }, [search, archived]);

  useEffect(() => {
    api.get("/admin/families").then(({ data }) => setFamilies(data));
    api.get("/admin/partners").then(({ data }) => setPartners(data));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load().catch((e) => toast.error(apiError(e))), 250);
    return () => clearTimeout(t);
  }, [load]);

  const save = async () => {
    try {
      const payload = { ...form, partner_id: form.partner_id || null };
      if (editingId) await api.put(`/admin/students/${editingId}`, payload);
      else await api.post("/admin/students", payload);
      toast.success(editingId ? "Student updated" : "Student added");
      setForm(null);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setArchive = async (student, value) => {
    await api.post(`/admin/students/${student.id}/archive?archived=${value}`);
    toast.success(value ? "Student archived" : "Student restored");
    load();
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/students/${confirmDelete.id}`);
      toast.success("Student deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <AppShell
      nav={ADMIN_NAV}
      title="Students"
      subtitle="All enrolled students and their record status"
      actions={
        <Button
          className="gap-2"
          onClick={() => {
            setForm({ ...blank, family_id: families[0]?.id || "" });
            setEditingId(null);
          }}
          data-testid="add-student-button"
        >
          <Plus className="h-4 w-4" /> Add student
        </Button>
      }
    >
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Search students by name, grade, year…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="student-search-input"
          />
        </div>
        <Button
          variant={archived ? "default" : "outline"}
          size="sm"
          onClick={() => setArchived(!archived)}
          data-testid="toggle-archived-students"
        >
          {archived ? "Viewing archived" : "View archived"}
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="overflow-x-auto p-0">
          {!students ? (
            <div className="p-6">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : (
            <Table data-testid="students-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>DOB</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Year</TableHead>
                  <TableHead>Family</TableHead>
                  <TableHead>Partner</TableHead>
                  <TableHead>Record status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {students.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-sm text-slate-500">
                      No students found.
                    </TableCell>
                  </TableRow>
                )}
                {students.map((s) => (
                  <TableRow key={s.id} data-testid={`student-row-${s.id}`}>
                    <TableCell className="font-medium text-slate-900">{s.legal_name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{s.date_of_birth || "—"}</TableCell>
                    <TableCell className="text-sm">{s.grade_level || "—"}</TableCell>
                    <TableCell className="text-sm">{s.academic_year || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{s.family_name || "—"}</TableCell>
                    <TableCell className="text-sm">{s.partner_name || "—"}</TableCell>
                    <TableCell>
                      <StatusBadge status={s.record_status} testId={`student-status-${s.id}`} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Link to={`/admin/records/student/${s.id}`} className="hidden" />
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Open record review"
                          onClick={async () => {
                            const { data } = await api.get(`/admin/students/${s.id}/record`);
                            window.location.href = `/admin/records/${data.id}`;
                          }}
                          data-testid={`review-student-${s.id}`}
                        >
                          <FileSearch className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setForm({
                              family_id: s.family_id,
                              partner_id: s.partner_id || "",
                              legal_name: s.legal_name,
                              date_of_birth: s.date_of_birth || "",
                              grade_level: s.grade_level || "",
                              academic_year: s.academic_year || "",
                              state_of_residence: s.state_of_residence || "",
                            });
                            setEditingId(s.id);
                          }}
                          data-testid={`edit-student-${s.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setArchive(s, !s.archived)}
                          data-testid={`archive-student-${s.id}`}
                        >
                          {s.archived ? (
                            <ArchiveRestore className="h-4 w-4" />
                          ) : (
                            <Archive className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600"
                          onClick={() => setConfirmDelete(s)}
                          data-testid={`delete-student-${s.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(form)} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit student" : "Add student"}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label>Student legal name</Label>
                <Input
                  value={form.legal_name}
                  onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                  data-testid="student-legal-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Date of birth</Label>
                <Input
                  type="date"
                  value={form.date_of_birth}
                  onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                  data-testid="student-dob-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Grade level</Label>
                <Input
                  value={form.grade_level}
                  onChange={(e) => setForm({ ...form, grade_level: e.target.value })}
                  data-testid="student-grade-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Academic year</Label>
                <Input
                  value={form.academic_year}
                  onChange={(e) => setForm({ ...form, academic_year: e.target.value })}
                  data-testid="student-academic-year-input"
                />
              </div>
              <div className="space-y-2">
                <Label>State of residence</Label>
                <Input
                  value={form.state_of_residence}
                  onChange={(e) => setForm({ ...form, state_of_residence: e.target.value })}
                  data-testid="student-residence-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Family</Label>
                <Select
                  value={form.family_id}
                  onValueChange={(v) => setForm({ ...form, family_id: v })}
                >
                  <SelectTrigger data-testid="student-family-select">
                    <SelectValue placeholder="Select family" />
                  </SelectTrigger>
                  <SelectContent>
                    {families.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.parent_name} ({f.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Educational partner</Label>
                <Select
                  value={form.partner_id}
                  onValueChange={(v) => setForm({ ...form, partner_id: v })}
                >
                  <SelectTrigger data-testid="student-partner-select">
                    <SelectValue placeholder="Select partner" />
                  </SelectTrigger>
                  <SelectContent>
                    {partners.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>
              Cancel
            </Button>
            <Button onClick={save} data-testid="save-student-button">
              Save student
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(confirmDelete)} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete student?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This permanently deletes {confirmDelete?.legal_name} and all uploaded records. This cannot
            be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={remove}
              data-testid="confirm-delete-student"
            >
              Delete student
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
